import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconUser, IconMapPin, IconArrowLeft } from '@tabler/icons-react';
import { db } from '../firebase';
import Footer from '../components/Footer';
import { doc, getDoc, updateDoc } from "firebase/firestore";
import './EditProfilePage.css';

function EditProfilePage({ user }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // Profile settings
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [location, setLocation] = useState('');
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  useEffect(() => {
    if (user) {
      loadUserSettings(user.uid);
    } else {
      setIsLoadingSettings(false);
    }
  }, [user]);

  const loadUserSettings = async (userId) => {
    setIsLoadingSettings(true);
    try {
      const userDocRef = doc(db, 'users', userId);
      const docSnap = await getDoc(userDocRef);
      if (docSnap.exists()) {
        const userData = docSnap.data();
        setDisplayName(userData?.displayName || user.displayName || '');
        setBio(userData?.bio || '');
        setLocation(userData?.location || '');
      } else {
        // Initialize with user auth data
        setDisplayName(user.displayName || '');
        setBio('');
        setLocation('');
      }
    } catch (error) {
      console.error("Error loading user settings:", error);
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const updateProfileSettings = async () => {
    if (!user) return;

    setIsSavingProfile(true);
    try {
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        displayName: displayName,
        bio: bio,
        location: location
      });
      alert("Profile updated successfully!");
      // Navigate back to profile
      if (user?.uid) {
        navigate(`/profile/${user.uid}`);
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      alert("Failed to update profile. Please try again.");
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (!user) {
    return (
      <div className="edit-profile-page">
        <div className="edit-profile-not-logged-in">
          <h2>{t('editProfile.signInRequired', 'Sign in required')}</h2>
          <p>{t('editProfile.signInToEdit', 'Please sign in to edit your profile.')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-profile-page">
      <div className="edit-profile-container">
        {/* Header with Back Button */}
        <div className="edit-profile-header">
          <button className="back-button" onClick={handleBack}>
            <IconArrowLeft size={20} />
            {t('editProfile.back', 'Back')}
          </button>
          <h1>{t('editProfile.title', 'Edit Profile')}</h1>
          <p className="edit-profile-subtitle">{t('editProfile.subtitle', 'Update your public profile information')}</p>
        </div>
        <div className="settings-divider"></div>

        {/* Profile Avatar Preview */}
        <div className="avatar-preview-section">
          <div className="avatar-preview">
            <img src={user.photoURL || '/default-avatar.png'} alt="Profile" />
          </div>
          <div className="avatar-info">
            <h3>{user.displayName || 'Your Name'}</h3>
            <p>@{user.email?.split('@')[0] || 'username'}</p>
          </div>
        </div>

        {/* Profile Form */}
        <div className="profile-form-card">
          <div className="profile-field">
            <label>{t('editProfile.displayName', 'Display Name')}</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={t('editProfile.displayNamePlaceholder', 'Your name')}
              disabled={isLoadingSettings}
              className="profile-input"
            />
          </div>

          <div className="profile-field">
            <label>{t('editProfile.bio', 'Bio')}</label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder={t('editProfile.bioPlaceholder', 'Tell others about yourself...')}
              rows={4}
              maxLength={150}
              disabled={isLoadingSettings}
              className="profile-textarea"
            />
            <span className="char-count">{bio.length}/150</span>
          </div>

          <div className="profile-field">
            <label>
              <IconMapPin size={16} />
              {t('editProfile.location', 'Location')}
            </label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder={t('editProfile.locationPlaceholder', 'Tokyo, Japan')}
              disabled={isLoadingSettings}
              className="profile-input"
            />
          </div>

          <div className="profile-actions">
            <button
              onClick={handleBack}
              className="cancel-button"
              disabled={isSavingProfile}
            >
              {t('editProfile.cancel', 'Cancel')}
            </button>
            <button
              onClick={updateProfileSettings}
              disabled={isSavingProfile || isLoadingSettings}
              className="save-profile-button"
            >
              {isSavingProfile ? t('editProfile.saving', 'Saving...') : t('editProfile.saveProfile', 'Save Profile')}
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default EditProfilePage;
